'use client';

import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  AirRingColors,
  FrontendBackendRingNameMap,
  productMap_air_ring,
  getRingAirColorNameImages,
  getAvailableAirRingColors,
  isRingAirSkuDisabled,
  RingSizes as RingSizesEnum,
  RingType,
} from 'content/products/ring';
import { CustomImage as Image } from 'components/shared';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import CustomLink from 'components/shared/CustomLink';
import { InfoIcon } from 'assets/icons';
import { GiftCardCodeInput } from './GiftCardCodeInput';
import styles from './ClaimForm.module.css';

const RingSizes = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

interface Country {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  phone_code: string;
}

interface State {
  id: number;
  name: string;
  iso2: string;
  state_code: string;
}

interface City {
  id: number;
  name: string;
  iso2: string;
}

export const DisclaimerText = () => (
  <p className={styles.disclaimerText}>
    <InfoIcon />
    <span>
      To explore other colour alternatives, please contact{' '}
      <CustomLink href="mailto:support@ultrahuman.com">
        support@ultrahuman.com
      </CustomLink>
    </span>
  </p>
);

export const Form = ({
  className,
  defaultColorByPage,
}: {
  className?: string;
  defaultColorByPage?: AirRingColors;
}) => {
    const t = useTranslations('ring');
    const RingAirColorNameImages = getRingAirColorNameImages(t);
    useGlobalUI({ liteUI: true });
    const router = useRouter();
    const searchParams = useSearchParams();
    const code = searchParams?.get('code');
    const [inputToken, setInputToken] = useState<string>('');
    const [countries, setCountries] = useState<Country[]>([]);
    const [states, setStates] = useState<State[]>([]);
    const [cities, setCities] = useState<City[]>([]);

    const [userToken, setUserToken] = useState<string>('');
    const [colorSelectionDisabled, setColorSelectionDisabled] =
      useState<boolean>(false);

    const [disabledSkus, setDisabledSkus] = useState<string[]>([]);
    const [shippingDisclaimer, setShippingDisclaimer] = useState<string>();
    const [selectionDisclaimer, setSelectionDisclaimer] = useState<string>();
    const [selectionDisclaimerValidColors, setSelectionDisclaimerValidColors] =
      useState<string[]>();

    const [isLoading, setLoading] = useState<boolean>(false);
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [manualSizeSelectVisible, setManualSizeSelectVisible] =
      useState<boolean>(false);

    const { getValues, setValue, watch } = useForm<{
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      address1: string;
      address2: string;
      postalCode: string;
      state: State | 'other' | undefined;
      stateTextValue?: string;
      city: string | 'other' | undefined;
      cityTextValue?: string;
      country: Country | undefined;
      size: number | undefined;
      color: AirRingColors | undefined;
      engraving?: string;
    }>({
      defaultValues: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address1: '',
        address2: '',
        postalCode: '',
        state: undefined,
        stateTextValue: undefined,
        city: undefined,
        cityTextValue: undefined,
        country: undefined,
        size: undefined,
        color: undefined,
        engraving: undefined,
      },
    });

    const selectedCountry = watch('country');
    const selectedState = watch('state');
    const selectedCity = watch('city');
    const selectedColor = watch('color');
    const selectedSize = watch('size');
    const stateTextValue = watch('stateTextValue');
    const cityTextValue = watch('cityTextValue');
    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const phone = watch('phone');
    const email = watch('email');
    const address1 = watch('address1');
    const address2 = watch('address2');
    const postalCode = watch('postalCode');

    const [formError, setFormError] = useState<boolean>(false);
    const [tokenErrorMessage, setTokenErrorMessage] = useState<string | null>(
      null,
    );
    const [verificationError, setVerificationError] = useState<string | null>(
      null,
    );

    const schema = z
      .object({
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        phone: z.string().min(6),
        email: z.string().email(),
        address1: z.string().min(1),
        address2: z.string().min(1),
        postalCode: z.string().min(1),
        state: z.union([z.any(), z.literal('other')]).refine((v) => !!v, {
          message: 'State is required',
        }),
        stateTextValue: z.string().optional(),
        city: z.union([z.string(), z.literal('other')]).refine((v) => !!v, {
          message: 'City is required',
        }),
        cityTextValue: z.string().optional(),
        country: z.any().refine((v) => !!v, { message: 'Country is required' }),
        size: z.number().int().min(1).optional(),
        color: z.nativeEnum(AirRingColors),
        engraving: z.string().max(8).optional(),
      })
      .superRefine((vals, ctx) => {
        if (vals.state === 'other' && !vals.stateTextValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['stateTextValue'],
            message: 'State is required',
          });
        }
        if (
          (vals.city === 'other' || vals.state === 'other') &&
          !vals.cityTextValue
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cityTextValue'],
            message: 'City is required',
          });
        }
      });

    const clearAllErrors = useCallback(() => {
      setTokenErrorMessage(null);
      setVerificationError(null);
      setFormError(false);
    }, []);

    const setVerificationErrorMessage = useCallback((message: string) => {
      setVerificationError(message);
      setTokenErrorMessage(null);
    }, []);

    const verifyToken = useCallback(
      async (token: string) => {
        clearAllErrors();
        setIsVerifying(true);

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/web_v1/validate_gift_card`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ serial_number: token }),
            },
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (
            data &&
            (data.code_valid === true ||
              data.valid === true ||
              data.success === true)
          ) {
            clearAllErrors();
            setUserToken(token);
            if (defaultColorByPage) {
              setValue('color', defaultColorByPage);
              setColorSelectionDisabled(true);
            } else if (data.default_color) {
              const defaultColor = FrontendBackendRingNameMap.getKey(
                data.default_color,
              );
              if (defaultColor && defaultColor in AirRingColors) {
                setValue('color', defaultColor as AirRingColors);
                setColorSelectionDisabled(true);
              }
            }

            if ('disabled_skus' in data && Array.isArray(data.disabled_skus)) {
              setDisabledSkus(data.disabled_skus);
            }

            if ('shipping_disclaimer' in data && data.shipping_disclaimer) {
              setShippingDisclaimer(data.shipping_disclaimer);
            }

            if ('selection_disclaimer' in data && data.selection_disclaimer) {
              setSelectionDisclaimer(data.selection_disclaimer);
            }

            if (
              'selection_disclaimer_valid_colors' in data &&
              data.selection_disclaimer_valid_colors
            ) {
              setSelectionDisclaimerValidColors(
                data.selection_disclaimer_valid_colors,
              );
            }
          } else {
            const errorMessage = data?.message || 'Gift card not found';
            setVerificationErrorMessage(errorMessage);
          }
        } catch (error) {
          setVerificationErrorMessage(
            'Unable to verify gift card. Please try again.',
          );
        } finally {
          setIsVerifying(false);
        }
      },
      [
        defaultColorByPage,
        setValue,
        clearAllErrors,
        setVerificationErrorMessage,
      ],
    );

    useEffect(() => {
      if (code && typeof code === 'string') {
        setInputToken(code as string);
      }
    }, [code]);

    useEffect(() => {
      fetchCountries();
    }, []);

    // Fetch states for India when component loads
    useEffect(() => {
      if (selectedCountry?.iso2 === 'IN') {
        fetchStates('IN');
      }
    }, [selectedCountry]);

    const fetchCountries = async () => {
      try {
        const indiaCountry: Country = {
          id: 101,
          name: 'India',
          iso2: 'IN',
          country_code: 'IN',
          phone_code: '+91',
        };
        setCountries([indiaCountry]);
        setValue('country', indiaCountry);
      } catch (error) {
        console.error('Error setting India as country:', error);
      }
    };

    const fetchStates = async (countryId: string) => {
      try {
        const response = await fetch(`/api/geo/states/${countryId}.json`);
        const data = await response.json();
        setStates(data);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };

    const fetchCities = async (stateId: string) => {
      try {
        const response = await fetch(`/api/geo/cities/${stateId}.json`);
        const data = await response.json();
        setCities(data);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    const handleCountryChange = (
      event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      const selectedOption = event.target.value;
      const found = countries.find((c) => c.name === selectedOption);
      setValue('country', found);
      setValue('state', undefined);
      setValue('stateTextValue', undefined);
      setValue('city', undefined);
      setValue('cityTextValue', undefined);
      fetchStates(found?.iso2 as string);
    };

    const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedOption = event.target.value;
      if (selectedOption === 'other') {
        setValue('state', 'other');
        setValue('stateTextValue', undefined);
        setValue('city', undefined);
        setValue('cityTextValue', undefined);
        return;
      }
      const st = states.find((s) => s.name === selectedOption);
      setValue('state', st);
      setValue('city', undefined);
      setValue('cityTextValue', undefined);
      fetchCities(st?.state_code as string);
    };

    const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedCityId = event.target.value;
      setValue('city', selectedCityId);
    };

    const handleManualSizeSelectShow = () => {
      setManualSizeSelectVisible(!manualSizeSelectVisible);
    };

    const onValid = async () => {
      clearAllErrors();
      setLoading(true);

      const formVals = getValues();

      const bodyData = {
        serial_number: userToken,
        email: formVals.email,
        phone_number: formVals.phone,
        color: FrontendBackendRingNameMap.get(
          formVals.color as unknown as string,
        ),
        size: formVals.size || null,
        engraving: formVals.engraving || undefined,
        shipping_address: {
          name: `${formVals.firstName} ${formVals.lastName}`,
          country: formVals.country?.name,
          country_code: formVals.country?.iso2,
          state:
            formVals.state !== 'other'
              ? (formVals.state as State)?.name
              : formVals.stateTextValue,
          city:
            formVals.state === 'other' || formVals.city === 'other'
              ? formVals.cityTextValue
              : (formVals.city as string),
          address1: formVals.address1,
          address2: formVals.address2,
          postal_code: formVals.postalCode,
        },
      } as const;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/web_v1/gift_card_ring_order`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyData),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          if (data && 'redirect_to' in data && data.redirect_to) {
            router.push(data.redirect_to);
            return;
          }
          router.push('/gift-card/success?status=success');
        } else {
          setVerificationErrorMessage(
            data.message || 'Unable to process your request. Please try again.',
          );
        }
      } catch (error) {
        setVerificationErrorMessage(
          'Unable to process your request. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    };

    const ringColors = useMemo(() => getAvailableAirRingColors(), []);

    return (
      <div className={className ? `${styles.root} ${className}` : styles.root}>
        {userToken ? (
          <form
            className={
              !userToken || !!tokenErrorMessage ? styles.disabled : ''
            }
            onSubmit={(e) => {
              e.preventDefault();
              const vals = getValues();
              const result = schema.safeParse({
                firstName,
                lastName,
                phone: phone as string,
                email: email as string,
                address1,
                address2,
                postalCode: postalCode as string,
                state: selectedState,
                stateTextValue,
                city: selectedCity as string,
                cityTextValue,
                country: selectedCountry,
                size: selectedSize,
                color: selectedColor,
                engraving: vals.engraving || '',
              });
              if (!result.success) {
                setFormError(true);
                return;
              }
              onValid();
            }}
          >
            <div className={styles.form}>
              <div className={styles.textDescription}>
                <h1>Claim your Ultrahuman Ring AIR Gift Card</h1>
                <p className={styles.titles}>
                  Enter your ring size, contact details, and delivery address to
                  claim your gift Ring AIR.
                </p>
              </div>
              <div className={styles.formContainer}>
                <div className={`${styles.contactDetails} ${styles.block}`}>
                  <p className={styles.titles}>Contact details</p>
                  <div className={styles.inputRows}>
                    <div className={styles.row}>
                      <label>
                        <span>
                          First Name <b>*</b>
                        </span>
                        <input
                          style={{}}
                          type="text"
                          name="firstName"
                          placeholder="Required field"
                          required
                          value={firstName}
                          onChange={(e) => {
                            setValue('firstName', e.target.value);
                            setFormError(false);
                          }}
                          disabled={isLoading}
                        />
                      </label>
                      <label>
                        <span>
                          Last Name <b>*</b>
                        </span>
                        <input
                          style={{}}
                          type="text"
                          name="lastName"
                          placeholder="Required field"
                          required
                          value={lastName}
                          onChange={(e) => {
                            setValue('lastName', e.target.value);
                            setFormError(false);
                          }}
                          disabled={isLoading}
                        />
                      </label>
                    </div>
                    <label>
                      <span>
                        Phone Number <b>*</b>
                      </span>
                      <input
                        type="tel"
                        style={{}}
                        inputMode="numeric"
                        name="phonenumber"
                        placeholder="Phone Number (required)"
                        required
                        value={phone}
                        pattern="^\+?\d*$"
                        onChange={(e) => {
                          if (!e.target.value.match(/^\+?\d*$/)) return;
                          setValue('phone', e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>
                    <label>
                      <span>
                        Email <b>*</b>
                      </span>
                      <input
                        style={{}}
                        type="email"
                        name="email"
                        placeholder="Email (required)"
                        required
                        value={email}
                        onChange={(e) => {
                          setValue('email', e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>
                  </div>
                </div>
                <div className={styles.block}>
                  <p className={styles.titles}>
                    Complimentary engraving (worth ₹3,299)
                  </p>
                  <p className={styles.subtitleStyle}>
                    Engraving orders take up to 7 days longer to ship
                  </p>
                  <input
                    type="text"
                    name="engraving"
                    placeholder="eg. Jon Snow"
                    maxLength={8}
                    onChange={(e) => setValue('engraving', e.target.value)}
                    disabled={isLoading}
                  />
                  <div className={styles.engravingHints}>
                    <span className={styles.characterLimit}>Max. 8 characters</span>
                    <span className={styles.characterSupport}>
                      (alphanumeric & some special characters supported)
                    </span>
                  </div>
                </div>
                <div className={`${styles.shippingDetails} ${styles.block}`}>
                  <p className={styles.titles}>Shipping details</p>
                  <p className={styles.disclaimer}>
                    Gift card claims are only available for addresses in India
                  </p>
                  {shippingDisclaimer ? (
                    <p className={styles.disclaimer}>{shippingDisclaimer}</p>
                  ) : null}

                  <div className={styles.inputRows}>
                    <div className={`${styles.selectWrapper} ${styles.disabled}`}>
                      <label>
                        <span>
                          Country <b>*</b>
                        </span>
                        <select
                          style={{}}
                          id="country"
                          value={selectedCountry?.name || ''}
                          onChange={(e) => {
                            handleCountryChange(e);
                            const selected = countries.find(
                              (c) => c.name === e.target.value,
                            );
                            setValue('country', selected);
                            setFormError(false);
                          }}
                          required
                          disabled={true}
                        >
                          <option value="">Select a country</option>
                          {countries.map((country) => (
                            <option key={country.iso2} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <label>
                      <span>
                        Address line 1 <b>*</b>
                      </span>
                      <input
                        type="text"
                        style={{}}
                        name="address1"
                        placeholder="Apartment, Suite, Company Name (required)"
                        required
                        value={address1}
                        onChange={(e) => {
                          setValue('address1', e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>

                    <label>
                      <span>
                        Address line 2 <b>*</b>
                      </span>
                      <input
                        style={{}}
                        type="text"
                        name="address2"
                        placeholder="Address (enter NA if not applicable)"
                        required
                        value={address2}
                        onChange={(e) => {
                          setValue('address2', e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>

                    <label>
                      <span>
                        Postal Code <b>*</b>
                      </span>
                      <input
                        style={{}}
                        type="text"
                        name="postalCode"
                        placeholder="Postal Code (required)"
                        required
                        value={postalCode}
                        onChange={(e) => {
                          setValue('postalCode', e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>

                    <div className={styles.row}>
                      <div
                        className={`${styles.selectWrapper} ${
                          !selectedCountry ? styles.disabled : ''
                        }`}
                      >
                        <label>
                          <span>
                            State <b>*</b>
                          </span>
                          <select
                            style={{}}
                            id="state"
                            value={
                              selectedState !== 'other'
                                ? (selectedState as State)?.name || ''
                                : 'other'
                            }
                            onChange={(e) => {
                              handleStateChange(e);
                              const selectedOption = e.target.value;
                              if (selectedOption === 'other') {
                                setValue('state', 'other');
                                setValue('city', undefined);
                                setValue('cityTextValue', undefined);
                              } else {
                                const selectedState = states.find(
                                  (s) => s.name === selectedOption,
                                );
                                setValue('state', selectedState);
                                setValue('city', undefined);
                                setValue('cityTextValue', undefined);
                              }
                              setFormError(false);
                            }}
                            disabled={!selectedCountry || isLoading}
                            required
                          >
                            <option value="">Select a state</option>
                            {states.map((state) => (
                              <option key={state.id} value={state.name}>
                                {state.name}
                              </option>
                            ))}
                            <option key={'other'} value={'other'}>
                              Other
                            </option>
                          </select>
                        </label>
                      </div>

                      {selectedState === 'other' ? (
                        <label>
                          <span>
                            State <b>*</b>
                          </span>
                          <input
                            style={{}}
                            type="text"
                            name="state"
                            placeholder="Enter a State"
                            required
                            value={stateTextValue ?? ''}
                            onChange={(e) => {
                              setValue('stateTextValue', e.target.value);
                              setFormError(false);
                            }}
                            disabled={isLoading}
                          />
                        </label>
                      ) : null}
                    </div>
                    <div className={styles.row}>
                      {selectedState !== 'other' ? (
                        <div
                          className={`${styles.selectWrapper} ${
                            !(selectedCountry && selectedState)
                              ? styles.disabled
                              : ''
                          }`}
                        >
                          <label>
                            <span>
                              City <b>*</b>
                            </span>
                            <select
                              style={{}}
                              id="city"
                              value={(selectedCity as string) || ''}
                              onChange={(e) => {
                                handleCityChange(e);
                                setValue('city', e.target.value);
                                setFormError(false);
                              }}
                              required
                              disabled={
                                !(selectedCountry && selectedState) || isLoading
                              }
                            >
                              <option value="">Select a city</option>
                              {cities.map((city) => (
                                <option key={city.id} value={city.iso2}>
                                  {city.name}
                                </option>
                              ))}
                              <option value="other">Other</option>
                            </select>
                          </label>
                        </div>
                      ) : null}

                      {selectedCity === 'other' || selectedState === 'other' ? (
                        <label>
                          <span>
                            City <b>*</b>
                          </span>
                          <input
                            style={{}}
                            type="text"
                            name="city"
                            placeholder="City"
                            required
                            value={cityTextValue ?? ''}
                            onChange={(e) => {
                              setValue('cityTextValue', e.target.value);
                              setFormError(false);
                            }}
                            disabled={isLoading}
                          />
                        </label>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div
                  className={`${styles.colorDetails} ${styles.block} ${
                    colorSelectionDisabled ? styles.disabled : ''
                  }`}
                >
                  {colorSelectionDisabled ? (
                    <p className={styles.titles}>Your pre-selected ring colour</p>
                  ) : (
                    <p className={styles.titles} style={{}}>
                      Select your ring color
                    </p>
                  )}

                  <div className={styles.colors}>
                    {ringColors.map((colorOption) => (
                      <button
                        disabled={
                          isLoading ||
                          (colorSelectionDisabled &&
                            selectedColor != colorOption)
                        }
                        type="button"
                        key={colorOption}
                        className={
                          selectedColor == colorOption ? styles.active : ''
                        }
                        onClick={() => {
                          setValue('color', colorOption);
                          setFormError(false);
                        }}
                      >
                        {/* Commented as this never occurs */}
                        {/* {colorOption === AirRingColors.ROSE_GOLD ? (
                          <div className="tag">NEW</div>
                        ) : null} */}
                        <Image
                          width={82}
                          height={80}
                          src={
                            RingAirColorNameImages[colorOption].image as string
                          }
                          alt={colorOption}
                        />
                        {RingAirColorNameImages[colorOption].name}
                      </button>
                    ))}
                  </div>
                  {/* Rose Gold upgrades removed for gift card redemption */}
                  {colorSelectionDisabled && !defaultColorByPage ? (
                    <DisclaimerText />
                  ) : null}
                </div>
                <div className={`${styles.sizingDetails} ${styles.block}`}>
                  <p className={styles.titles} style={{}}>
                    Select your size
                  </p>

                  {/* Sizing Kit Option */}
                  <div className={styles.sizingKitSection}>
                    <h4>Not sure about your Ultrahuman Ring size?</h4>
                    <button
                      type="button"
                      className={`${styles.sizingKitButton} ${
                        selectedSize === undefined ? styles.selected : ''
                      }`}
                      onClick={() => {
                        setValue('size', undefined);
                        setFormError(false);
                        setManualSizeSelectVisible(false);
                      }}
                      disabled={isLoading}
                    >
                      <Image
                        src={'web/ring-buy/sizing-kit.webp'}
                        width={50}
                        height={50}
                        alt="sizing_kit"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className={styles.content}>
                        <div className={styles.title}>Opt for a sizing kit</div>
                        <div className={styles.subtitle}>
                          Use the sample rings to know the best size for you.
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Toggle Button for Manual Size Selection */}
                  <button
                    type="button"
                    className={styles.manualSizeToggle}
                    onClick={handleManualSizeSelectShow}
                  >
                    I have a sizing kit
                    <span
                      className={`${styles.toggleArrow} ${
                        manualSizeSelectVisible ? styles.open : ''
                      }`}
                    >
                      ▼
                    </span>
                  </button>

                  {/* Size Options - Collapsible */}
                  <div
                    className={`${styles.manualSizeSelectContainer} ${
                      manualSizeSelectVisible ? styles.visible : ''
                    }`}
                  >
                    <div className={styles.sizes}>
                      {RingSizes.map((sizeOption) => (
                        <button
                          type="button"
                          key={sizeOption}
                          className={
                            selectedSize == sizeOption ? styles.active : ''
                          }
                          onClick={() => {
                            setValue('size', sizeOption);
                            setFormError(false);
                          }}
                          disabled={
                            isLoading ||
                            isRingAirSkuDisabled({
                              color: selectedColor as AirRingColors,
                              size: sizeOption as RingSizesEnum,
                              type: RingType.NOT_ENGRAVED,
                            }) ||
                            disabledSkus.includes(
                              productMap_air_ring[
                                selectedColor as AirRingColors
                              ]?.[sizeOption as RingSizesEnum]?.NOT_ENGRAVED
                                .SKU as string,
                            )
                          }
                        >
                          {sizeOption}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Rose Gold refund disclaimer removed */}

                  {selectedColor &&
                  selectionDisclaimer &&
                  selectionDisclaimerValidColors?.includes(
                    FrontendBackendRingNameMap.get(
                      selectedColor as AirRingColors,
                    ) as string,
                  ) ? (
                    <p className={styles.disclaimer}>{selectionDisclaimer}</p>
                  ) : null}
                </div>
                <button
                  className={styles.submit}
                  type="submit"
                  disabled={
                    isLoading ||
                    !!tokenErrorMessage ||
                    !!verificationError ||
                    formError
                  }
                >
                  {isLoading ? 'Submitting...' : 'Claim Ring AIR'}
                </button>
                <div className={styles.errorMessage}>
                  <p>
                    {formError ? (
                      <>{'Please re-check and fill the form properly.'}</>
                    ) : tokenErrorMessage || verificationError ? (
                      <>{tokenErrorMessage || verificationError}</>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className={styles.form}>
            <div className={styles.textDescription}>
              <h1>Claim your Ultrahuman Ring AIR Gift Card</h1>
              <p className={styles.titles}>
                Enter your ring size, contact details, and delivery address to
                claim your gift Ring AIR.
              </p>
            </div>
            <div className={styles.formContainer}>
              <div className={`${styles.contactDetails} ${styles.block}`}>
                <p>Enter Gift Code</p>
                <GiftCardCodeInput
                  value={inputToken}
                  onChange={(value) => {
                    setInputToken(value);
                  }}
                  onFocus={() => {
                    clearAllErrors();
                  }}
                  onError={setTokenErrorMessage}
                  disabled={isLoading || isVerifying}
                />
                <div className={styles.errorMessage}>
                  {tokenErrorMessage || verificationError ? (
                    <p>{tokenErrorMessage || verificationError}</p>
                  ) : null}
                </div>
              </div>
              <button
                className={styles.submit}
                disabled={
                  !inputToken ||
                  inputToken.length !== 16 ||
                  !!tokenErrorMessage ||
                  !!verificationError ||
                  isLoading ||
                  isVerifying
                }
                onClick={() => {
                  const cleanCode = inputToken.replace(/-/g, '');
                  if (
                    cleanCode.length === 16 &&
                    /^[A-Z0-9]+$/.test(cleanCode)
                  ) {
                    verifyToken(inputToken);
                  } else {
                    setTokenErrorMessage(
                      'Please enter a valid 16-character gift card code',
                    );
                  }
                }}
              >
                {isVerifying ? 'Verifying...' : 'Continue'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
};
